import liteguid from "lite-guid";
import GdprStorage from "../storage/GdprStorage";
import { ApiUrlUtil } from "../utils/ApiUrlUtil";
import TrackingUtils from "./TrackingUtils";
import type { TrackingSessionData, TrackingEvent } from "./TrackingUtils";
type State = {
	eventQueue: Array<TrackingEvent>;
};
const TRACKING_COOKIE = "tsession";
// 1 * 60 * 60 * 1000 leads to the cookie being valid one hour from now.
const HOUR_IN_MILLIS = 60 * 60 * 1000;
const EVENT_QUEUE_SIZE_LIMIT = 1000;
let isRequesting = false;

class TrackingQueue {
	state: State;

	constructor() {
		this.resetState();
	}

	resetState() {
		this.state = this.getEmptyState();
	}

	getEmptyState(): State {
		return {
			eventQueue: [],
		};
	}

	getEventQueueLength(): number {
		return EVENT_QUEUE_SIZE_LIMIT;
	}

	getTimeStamp(): Promise<string> {
		return new Promise((resolve, reject) => {
			ApiUrlUtil.manualXMLHttpRequest(
				"/explore.tracking.v1/health",
				"HEAD",
				null,
				xmlHttp => resolve(xmlHttp.getResponseHeader("Date")),
				() => reject("http request failed"),
			);
		});
	}

	writeCookieData(newSessionData: TrackingSessionData) {
		const expirationDate = new Date(new Date().getTime() + HOUR_IN_MILLIS);
		GdprStorage.cookie.put(TRACKING_COOKIE, JSON.stringify(newSessionData), GdprStorage.NECESSARY, {
			expires: expirationDate,
		});
	}

	getSessionDataFromCookie(): TrackingSessionData | null | undefined {
		const cookieData = GdprStorage.cookie.read(TRACKING_COOKIE);

		if (typeof cookieData === "string") {
			try {
				return JSON.parse(cookieData);
			} catch (err) {
				console.warn("not able to parse session cookie: ", err);
			}
		}
	}

	async getOrCreateCookieData(): Promise<TrackingSessionData> {
		const sessionData: TrackingSessionData | null | undefined = this.getSessionDataFromCookie();

		if (sessionData) {
			return sessionData;
		} else {
			isRequesting = true;

			try {
				const serverDate = await this.getTimeStamp();
				const newSessionData: TrackingSessionData = {
					sessionId: liteguid.create(),
					timestamp: new Date(serverDate).getTime(),
				};
				isRequesting = false;
				this.writeCookieData(newSessionData);
				return newSessionData;
			} catch {
				isRequesting = false;
				return Promise.reject("failed to get or set session cookie data");
			}
		}
	}

	processEvent(sessionData: TrackingSessionData) {
		// due to async processing, we have to check *here* whether the queue still contains events
		if (this.state.eventQueue.length !== 0) {
			const nextEvent: TrackingEvent | undefined = this.state.eventQueue.shift();
			if (!nextEvent) {
				return;
			}
			if (nextEvent.eventType === "page") {
				this.writeCookieData(sessionData);
			}

			const image = new Image();
			image.src = TrackingUtils.generateStructuredEventUrl(nextEvent, sessionData);
			this.processQueue();
		}
	}

	processQueue() {
		if (!isRequesting) {
			const sessionDataPromise = this.getOrCreateCookieData();
			sessionDataPromise.then(sessionData => this.processEvent(sessionData)).catch(msg => console.warn(msg));
		}
	}

	preventQueueOverflow() {
		if (this.state.eventQueue.length >= EVENT_QUEUE_SIZE_LIMIT) {
			const droppedEvents = this.state.eventQueue.splice(0, 10);
			const droppedEvent = droppedEvents.shift();
			if (!droppedEvent) {
				return;
			}
			droppedEvent.eventType = "app.trackingEventsDropped";
			droppedEvent.structuredData = {};
			this.state.eventQueue.push(droppedEvent);
		}
	}

	enqueueTrackingEvent(trackingEvent: TrackingEvent) {
		// Allow opt-out from tracking for legal reasons.
		const trackingNotAllowed =
			!GdprStorage.isCategoryAllowed(GdprStorage.PERFORMANCE, "tracking", "READ") &&
			!window.user_has_agreed_to_be_tracked;

		if (trackingNotAllowed) {
			return;
		}

		this.preventQueueOverflow();
		this.state.eventQueue.push(trackingEvent);
		this.processQueue();
	}
}

export default new TrackingQueue() as TrackingQueue;
